import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import { SHOPS } from "@/data";
import { Alpana } from "@/components/Logo";
import PotShop from "@/shop/PotShop";

export default function Shop({ shopKey = "potchitra", saved, toggleSave, navTo, site }) {
  // পটচিত্র ঘর — নতুন দোকান-অভিজ্ঞতা (প্রতিনিধি + তাক + ট্রাই-অন)
  if (shopKey === "potchitra") {
    return <PotShop saved={saved} toggleSave={toggleSave} navTo={navTo} site={site} />;
  }
  return <ShopGrid shopKey={shopKey} saved={saved} toggleSave={toggleSave} navTo={navTo} site={site} />;
}

function ShopGrid({ shopKey = "mrityo", saved, toggleSave, navTo, site }) {
  const [active, setActive] = useState(null);
  const siteShop = site?.shops?.find((s) => s.key === shopKey);
  const siteProducts = site?.products
    ?.filter((p) => p.shop_key === shopKey)
    .map((p) => ({ id: p.id, title: p.title, latin: p.latin, price: p.price, artisan: p.artisan, img: p.img, tag: p.tag }));
  const base = SHOPS[shopKey] || SHOPS.potchitra;
  const shop = siteShop
    ? {
        ...base,
        name: siteShop.name,
        latin: siteShop.latin,
        blurb: siteShop.blurb,
        img: siteShop.image || base.img,
        products: siteProducts?.length ? siteProducts : base.products,
      }
    : base;
  const products = shop.products;

  return (
    <div>
      <section className="relative flex h-[58vh] min-h-[420px] items-end overflow-hidden">
        <img src={shop.img} alt={shop.name} data-testid="shop-hero-image" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/30 to-dusk/55" />
        <div className="night-glow" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-12 sm:px-10">
          <motion.button
            data-testid="back-to-haat"
            onClick={() => navTo("hat", "রঙের হাট")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-7 flex items-center gap-2 rounded-full border border-sand/25 bg-dusk/40 px-4 py-1.5 text-xs text-sand/85 backdrop-blur-md transition-colors hover:border-gold/60 hover:text-gold"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            হাটের পথে ফিরুন
          </motion.button>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-latin text-xs italic tracking-[0.3em] text-gold/85 drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]"
          >
            {shop.latin}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-display text-5xl text-alpana sm:text-6xl"
          >
            {shop.name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-3 max-w-xl text-sm leading-relaxed text-sand/75"
          >
            {shop.blurb}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-14 sm:px-10 sm:py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => {
            const isSaved = saved.includes(p.id);
            return (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: (i % 3) * 0.09, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="group cursor-pointer overflow-hidden rounded-2xl border border-gold/15 bg-earthen/70 transition-colors hover:border-gold/40"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dusk/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="absolute left-4 top-4 rounded-full border border-gold/25 bg-dusk/60 px-3 py-1 text-[10px] tracking-wide text-gold/90 backdrop-blur-md">
                    {p.tag}
                  </span>
                  <button
                    data-testid={`product-save-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSave(p.id);
                    }}
                    aria-label={isSaved ? "থলি থেকে বাদ দিন" : "থলিতে জমান"}
                    className="absolute right-4 top-4 rounded-full border border-gold/25 bg-dusk/60 p-2.5 backdrop-blur-md transition-transform duration-300 hover:scale-110"
                  >
                    <Heart size={16} className={isSaved ? "fill-vermilion text-vermilion" : "text-sand/80"} />
                  </button>
                  <span className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-3 whitespace-nowrap rounded-full bg-alpana/95 px-5 py-2 text-xs font-medium text-dusk opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    কাছাকাছি দেখুন
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl leading-snug text-sand">{p.title}</h3>
                  <p className="mt-1 text-xs text-sand/50">{p.artisan}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-lg font-semibold text-gold">{p.price}</span>
                    <span className="font-latin text-xs italic text-sand/40">{p.latin}</span>
                  </div>
                </div>
              </motion.article>
            );
          })}

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: 0.18, duration: 0.7 }}
            className="relative flex min-h-[300px] flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-gold/20 bg-earthen/40 p-8 text-center"
          >
            <Alpana className="w-28 opacity-20" />
            <h3 className="mt-5 font-display text-xl text-sand/55">আরও শিল্পীদের ঘর আসছে</h3>
            <p className="mt-2 text-xs leading-relaxed text-sand/40">
              এই হাটে শীঘ্রই বাঁশশিল্প আর চামচিল্পের ঘর খুলবে
            </p>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[85] flex items-center justify-center bg-dusk/85 p-4 backdrop-blur-md sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="product-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 26, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative grid max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gold/25 bg-plaster md:grid-cols-2"
            >
              <button
                data-testid="product-modal-close"
                onClick={() => setActive(null)}
                className="absolute right-4 top-4 z-10 rounded-full bg-dusk/70 p-2 text-sand/70 backdrop-blur-md transition-colors hover:text-sand"
                aria-label="বন্ধ করুন"
              >
                <X size={16} />
              </button>
              <img src={active.img} alt={active.title} className="h-64 w-full object-cover md:h-full md:min-h-[420px]" />
              <div className="flex flex-col p-7">
                <span className="w-max rounded-full border border-gold/25 px-3 py-1 text-[10px] tracking-wide text-gold/90">
                  {active.tag}
                </span>
                <h3 className="mt-4 font-display text-2xl leading-snug text-sand">{active.title}</h3>
                <p className="mt-1 font-latin text-sm italic text-sand/45">{active.latin}</p>
                <p className="mt-3 text-sm text-sand/60">{active.artisan}</p>
                <p className="mt-4 font-display text-3xl text-gold">{active.price}</p>
                <p className="mt-5 text-sm leading-relaxed text-sand/60">
                  এই শিল্পকর্ম এখনই কেনা যাবে না — প্রোটোটাইপ পর্ব। পছন্দ হলে থলিতে জমিয়ে রাখুন, হাট পূর্ণ রূপ পেলেই চেকআউট আসবে।
                </p>
                <div className="mt-auto flex flex-wrap gap-3 pt-6">
                  <button
                    data-testid="product-modal-save"
                    onClick={() => toggleSave(active.id)}
                    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all duration-300 ${
                      saved.includes(active.id)
                        ? "bg-vermilion text-alpana"
                        : "border border-gold/40 text-gold hover:bg-gold hover:text-dusk"
                    }`}
                  >
                    <Heart size={15} className={saved.includes(active.id) ? "fill-alpana" : ""} />
                    {saved.includes(active.id) ? "থলিতে জমা আছে" : "থলিতে জমান"}
                  </button>
                  <button
                    onClick={() => setActive(null)}
                    className="rounded-full border border-sand/25 px-5 py-2.5 text-sm text-sand/80 transition-colors hover:bg-sand/10"
                    data-testid="product-modal-back"
                  >
                    ঘর ঘুরে দেখি
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
